import { Classes } from "@blueprintjs/core";
import React, { useState } from "react";
import styled from "styled-components";

type Props = {
  email?: string;
  profileUrl?: string;
  displayName?: string;
  isCircle?: boolean;
  size?: string;
  onlyAvatar?: boolean;
  isCurrentUser?: boolean;
  position?: string;
  isLoading?: boolean;
};

const Avatar = (props: Props) => {
  const {
    email,
    profileUrl,
    displayName,
    isCircle,
    isCurrentUser = false,
    position = "start",
    onlyAvatar = false,
    isLoading = false,
    size,
  } = props;
  let src = profileUrl;
  if (!src && onlyAvatar) src = email ? getAvatarUrl(email) : undefined;
  const [hasImageError, setHasImageError] = useState(false);

  const handleError = () => {
    setHasImageError(true);
  };

  const avatarUrl = React.useMemo(
    () => hasImageError && displayName && createInitialsAvatar(displayName),
    [hasImageError, displayName]
  );

  if (isLoading) {
    return (
      <Wrapper iscurrentuser={isCurrentUser} className={Classes.SKELETON} position={position}>
        <CircularImage />
      </Wrapper>
    );
  }

  return (
    <Wrapper iscurrentuser={isCurrentUser} position={position}>
      {!hasImageError && src ? (
        <CircularImage
          alt={displayName}
          onError={handleError}
          src={src}
          iscircle={!!isCircle}
          size={size}
        />
      ) : avatarUrl ? (
        <CircularImage
          src={avatarUrl}
          alt={`${displayName} avatar`}
          iscircle={!!isCircle}
          size={size}
        />
      ) : (
        <div className="inner-circle">N/A</div>
      )}
    </Wrapper>
  );
};

export default Avatar;

const CircularImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== "iscircle" && prop !== "size",
})<{ iscircle?: boolean; size?: string }>`
  width: ${(props) => (props.size ? props.size : "32px")};
  height: ${(props) => (props.size ? props.size : "32px")};
  object-fit: fill;
  border-radius: ${({ iscircle }) => (iscircle ? "50%" : "6px")};
  box-shadow: inset 0 0 0 1px hsla(0, 0%, 0%, 0.1);
  padding: 1px;
`;
const Wrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "iscurrentuser",
})<{ iscurrentuser: boolean, position?: string }>`
  display: flex;
  align-self: ${(props) => {
    return props.position && props.position=="end" ? "flex-end" : "flex-start";
  }};
  margin-right: ${(props) => {
    return props.iscurrentuser ? 0 : "6px";
  }};
  margin-left: ${(props) => {
    return props.iscurrentuser ? "6px" : 0;
  }};
`;

const getAvatarUrl = (email: string) =>
  `https://avatars.apple.com/a?mail=${encodeURIComponent(email)}`;

const getBackgroundColor = (char: string): string => {
  const colors: { [key: string]: string } = {
    A: "#1F1B24",
    B: "#2D2B4E",
    C: "#4A2849",
    D: "#301934",
    E: "#343749",
    F: "#5D2D50",
    G: "#4A4D6B",
    H: "#2E2233",
    I: "#3D3B51",
    J: "#2B283E",
    K: "#232040",
    L: "#241F33",
    M: "#352A3B",
    N: "#3C2F3A",
    O: "#493F53",
    P: "#524667",
    Q: "#402D4E",
    R: "#33253A",
    S: "#4F2B4B",
    T: "#3A2A44",
    U: "#3B2C5D",
    V: "#4D2F65",
    W: "#282036",
    X: "#30243E",
    Y: "#2E2747",
    Z: "#2B1D3B",
    default: "#1A1A1A", // Default dark color
  };

  return colors[char.toUpperCase()] || colors.default;
};

const createInitialsAvatar = (displayName: string): string => {
  const initials = displayName
    .split(" ")
    .map((part) => part[0].toUpperCase())
    .join("")
    .slice(0, 2);

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = 100;
  canvas.height = 100;
  if (context) {
    context.fillStyle = getBackgroundColor(initials); // Random background color
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#ffffff"; // Text color
    context.font = "50px Arial";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(initials, canvas.width / 2, canvas.height / 2 + 6);
  }
  return canvas.toDataURL();
};
